import * as React from 'react';

interface Position {
  x: number;
  y: number;
}

interface UseDraggableProps {
  initialPosition: Position;
  disabled?: boolean;
}

interface UseDraggableReturn {
  position: Position;
  isDragging: boolean;
  containerRef: React.RefObject<HTMLDivElement>;
  handleMouseDown: (e: React.MouseEvent) => void;
  handleTouchStart: (e: React.TouchEvent) => void;
  wasDragged: boolean;
}

export const useDraggable = ({
  initialPosition,
  disabled = false,
}: UseDraggableProps): UseDraggableReturn => {
  const [position, setPosition] = React.useState(initialPosition);
  const [isDragging, setIsDragging] = React.useState(false);
  const [dragOffset, setDragOffset] = React.useState({ x: 0, y: 0 });
  const [wasDragged, setWasDragged] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const dragStartPosition = React.useRef<Position>({ x: 0, y: 0 });

  const constrainPosition = React.useCallback((x: number, y: number) => {
    const container = containerRef.current;
    if (!container) return { x, y };

    const rect = container.getBoundingClientRect();
    const maxX = globalThis.innerWidth - rect.width;
    const maxY = globalThis.innerHeight - rect.height;

    return {
      x: Math.max(0, Math.min(x, maxX)),
      y: Math.max(0, Math.min(y, maxY)),
    };
  }, []);

  const handleMouseDown = React.useCallback(
    (e: React.MouseEvent) => {
      if (disabled) return;

      setIsDragging(true);
      setWasDragged(false);
      dragStartPosition.current = { x: e.clientX, y: e.clientY };
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) {
        setDragOffset({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
        });
      }
      e.preventDefault();
    },
    [disabled]
  );

  const handleTouchStart = React.useCallback(
    (e: React.TouchEvent) => {
      if (disabled) return;

      setIsDragging(true);
      setWasDragged(false);
      if (e.touches.length > 0) {
        const touch = e.touches.item(0);
        if (touch) {
          dragStartPosition.current = { x: touch.clientX, y: touch.clientY };
          const rect = containerRef.current?.getBoundingClientRect();
          if (rect) {
            setDragOffset({
              x: touch.clientX - rect.left,
              y: touch.clientY - rect.top,
            });
          }
        }
      }
      e.preventDefault();
    },
    [disabled]
  );

  React.useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;

      // Check if this is actual dragging (moved more than a few pixels)
      const deltaX = Math.abs(e.clientX - dragStartPosition.current.x);
      const deltaY = Math.abs(e.clientY - dragStartPosition.current.y);

      if (deltaX > 5 || deltaY > 5) {
        setWasDragged(true);
      }

      const newPosition = constrainPosition(
        e.clientX - dragOffset.x,
        e.clientY - dragOffset.y
      );
      setPosition(newPosition);
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isDragging) return;

      if (e.touches.length === 0) return;

      const touch = e.touches.item(0);
      if (!touch) return;

      // Check if this is actual dragging (moved more than a few pixels)
      const deltaX = Math.abs(touch.clientX - dragStartPosition.current.x);
      const deltaY = Math.abs(touch.clientY - dragStartPosition.current.y);

      if (deltaX > 5 || deltaY > 5) {
        setWasDragged(true);
      }

      const newPosition = constrainPosition(
        touch.clientX - dragOffset.x,
        touch.clientY - dragOffset.y
      );
      setPosition(newPosition);
      e.preventDefault();
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    const handleTouchEnd = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, {
        passive: false,
      });
      document.addEventListener('touchend', handleTouchEnd);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, dragOffset, constrainPosition]);

  return {
    position,
    isDragging,
    containerRef,
    handleMouseDown,
    handleTouchStart,
    wasDragged,
  };
};
